import { useMemo, useRef, useEffect } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import {
  getReportingPeriods,
  createReportingPeriod,
  updateReportingPeriod,
} from "@/api/ReportingPeriods";
import {
  ReportingPeriodAtom,
  SelectedReportingPeriodAtom,
} from "@/store/ReportingPeriodStateAtom";
import moment from "moment-timezone";

interface UseReportingPeriodTimelineController {
  selectedFund: ReturnType<typeof useAtomValue<typeof SelectedFundAtom>>;
  selectedReportingPeriod: ReturnType<
    typeof useAtomValue<typeof SelectedReportingPeriodAtom>
  >;
  timelineRef: React.RefObject<HTMLDivElement | null>;
  periods: ReportingPeriod[];
  isLoading: boolean;
  periodsWithSpacing: (ReportingPeriod & { daysSincePrevious: number })[];
  handleSelectPeriod: (period: ReportingPeriod) => void;
  createPeriod: (values: { period: Date; isOpen: boolean }) => Promise<void>;
  updatePeriod: (period: ReportingPeriod) => Promise<void>;
}

export const useReportingPeriodTimelineController =
  (): UseReportingPeriodTimelineController => {
    const selectedFund = useAtomValue(SelectedFundAtom);
    const setSelectedReportingPeriod = useSetAtom(SelectedReportingPeriodAtom);
    const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
    const timelineRef = useRef<HTMLDivElement | null>(null);
    const [reportingPeriods, setReportingPeriods] =
      useAtom(ReportingPeriodAtom);

    const {
      data: periods = [],
      isLoading,
      refetch,
    } = useQuery({
      queryKey: ["reportingPeriods", selectedFund?.fundId],
      queryFn: async () => {
        const data = await getReportingPeriods(
          selectedFund?.fundId?.toString() || ""
        );
        setReportingPeriods(data);
        return data;
      },
      enabled: !!selectedFund?.fundId,
    });

    // Sort periods by date (oldest first)
    const sortedPeriods = [...periods].sort(
      (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
    );

    // Calculate days between periods for visual spacing
    const periodsWithSpacing = useMemo(() => {
      return sortedPeriods.map((period, index, array) => {
        let daysSincePrevious = 0;
        if (index > 0) {
          daysSincePrevious = moment(period.period).diff(
            moment(array[index - 1].period),
            "days"
          );
        }

        return {
          ...period,
          daysSincePrevious,
        };
      });
    }, [periods]);

    // Auto-select the most recent reporting period if none is selected
    useEffect(() => {
      if (!selectedReportingPeriod && periodsWithSpacing.length > 0) {
        const mostRecentPeriod = periodsWithSpacing[periodsWithSpacing.length - 1];
        setSelectedReportingPeriod(mostRecentPeriod);
      }
    }, [selectedReportingPeriod, periodsWithSpacing, setSelectedReportingPeriod]);

    const createPeriod = async (values: { period: Date; isOpen: boolean }) => {
      console.log("selectedFund", selectedFund);
      if (!selectedFund?.fundId) {
        throw new Error("No fund selected");
      }

      try {
        const result = await createReportingPeriod(selectedFund.fundId, {
          period: values.period.toISOString(),
          isOpen: values.isOpen,
        });

        // Set the newly created period as the selected period if returned
        if (result) {
          setSelectedReportingPeriod(result);
          refetch();
        }
      } catch (error) {
        console.error("Failed to create reporting period:", error);
        throw error;
      }
    };

    const updatePeriod = async (period: ReportingPeriod) => {
      if (!selectedFund?.fundId) {
        throw new Error("No fund selected");
      }

      try {
        const result = await updateReportingPeriod(period);
        if (result) {
          refetch();
        }
      } catch (error) {
        console.error("Failed to update reporting period:", error);
        throw error;
      }
    };

    return {
      selectedFund,
      selectedReportingPeriod,
      timelineRef,
      periods,
      isLoading,
      periodsWithSpacing,
      handleSelectPeriod: setSelectedReportingPeriod,
      createPeriod,
      updatePeriod,
    };
  };
